import { createContext, useContext, useEffect, useState, ReactNode } from "react";
import { labData } from "./lab-data";
import { getSession, signIn as sloeSignIn, signOut as sloeSignOut } from "./sloeAuthClient";
import { listBuilds, loadBuild, deleteBuild } from "./build-storage";
import { assignSafeSlug } from "../../shared/reserved-slugs";

// The Studio's user = the Lab user the SloeAuth BFF sealed into the shared
// .sloelabs.com session (id = public.users.id, the same id Lab accounts always had).
export interface User {
  id: string;
  email: string;
  user_metadata?: { full_name?: string };
}
export interface Session {
  user: User;
  expires_at?: string;
}

interface AuthState {
  user: User | null;
  session: Session | null;
  isLoading: boolean;
  /** Send the browser to SloeAuth (Google or an email code) and back here. */
  signIn: (returnTo?: string) => void;
  /** Revoke-first sign-out; false = SloeAuth could not confirm it. */
  signOut: () => Promise<boolean>;
}

const AuthContext = createContext<AuthState | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    getSession().then((s) => {
      if (cancelled) return;
      const u: User | null = s ? { id: s.user_id, email: s.email, user_metadata: s.name ? { full_name: s.name } : {} } : null;
      setUser(u);
      setSession(u ? { user: u, expires_at: s?.expires_at } : null);
      if (u) migrateAnonymousData(u.id);
      setIsLoading(false);
    });
    return () => { cancelled = true; };
  }, []);

  const signIn = (returnTo?: string) => sloeSignIn(returnTo);
  const signOut = async () => {
    const ok = await sloeSignOut(window.location.href);
    if (ok) { setUser(null); setSession(null); }
    return ok;
  };

  return (
    <AuthContext.Provider value={{ user, session, isLoading, signIn, signOut }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
}

// ═══════════════════════════════════════════════════════════════════════
// Anonymous -> Authenticated Migration
// ═══════════════════════════════════════════════════════════════════════
// When a user signs in, we check their localStorage for any anonymous builds.
// If found, we migrate them to the 'operating_systems' table (as the signed-in
// user, RLS: owner_id = current_user_id()) and clear them from localStorage.

// §P0-2 — Module-level guard: prevent concurrent migrations from firing for
// the same user (a remount would otherwise race and cause 409s before UPSERT lands).
const _migrationsInFlight = new Set<string>();

async function migrateAnonymousData(userId: string) {
  if (_migrationsInFlight.has(userId)) {
    console.log(`[Migration] Skipping: already in flight for ${userId}`);
    return;
  }
  _migrationsInFlight.add(userId);
  try {
  const localBuilds = listBuilds();
  if (localBuilds.length === 0) return;

  console.log(`[Migration] Found ${localBuilds.length} anonymous builds. Migrating to user ${userId}...`);

  for (const summary of localBuilds) {
    const fullBuild = loadBuild(summary.id);
    if (!fullBuild) continue;

    // §P0-2 — Map the local SavedBuild into the Operating System schema, including a stable
    // local_build_id so retries are idempotent. UPSERT keyed on (owner_id, local_build_id)
    // means the same build can't get migrated twice even if two loads race.
    const osPayload = {
      owner_id: userId,
      local_build_id: summary.id,
      subdomain_slug: assignSafeSlug(fullBuild.company_name),
      os_name: fullBuild.company_name || "Untitled Operating System",
      industry_shape: fullBuild.industry,
      active_schema: {
        architectState: fullBuild.architectState,
        inferredStyle: fullBuild.inferredStyle,
        chatHistory: fullBuild.messages,
      },
      is_deployed: false,
    };

    const { error } = await labData
      .from('operating_systems')
      .upsert(osPayload, { onConflict: 'owner_id,local_build_id' });

    if (!error) {
      console.log(`[Migration] Successfully migrated build ${summary.id}`);
      deleteBuild(summary.id); // Clear local copy after successful migration
    } else {
      console.error(`[Migration] Failed to migrate build ${summary.id}:`, error);
    }
  }
  } finally {
    _migrationsInFlight.delete(userId);
  }
}

